import { withTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

import CodeUtil from "@plannel/pages/utils/code-util";
import numberUtil from "@plannel/utils/number-util";

import { FLO_KIND } from "./floConstants";

// 노드 종류별 사이드바에 노출할 기본 속성(컬럼). 백엔드 응답 필드(camelCase) 기준.
const FIELDS = {
  [FLO_KIND.ITEM]: ["itemCd", "name", "itemType", "uomCd", "stdUnitPrice"],
  [FLO_KIND.ROUTE]: ["routeCd", "name", "altResPolicyCd", "discreteFlg"],
  [FLO_KIND.RESOURCE]: [
    "resCd",
    "name",
    "workcenterCd",
    "locCd",
    "locName",
    "nopCalGrpName",
    "workHourPerDay",
    "workDayPerWeek",
    "resCnt",
  ],
};

// DTO 필드명과 언어팩 키가 다른 필드 — 라벨 표시는 기존 번역 키를 재사용한다.
const FIELD_LABEL_KEY = {
  workHourPerDay: "whpd",
  workDayPerWeek: "wdpw",
  nopCalGrpName: "calendarGrpName",
};

// 금액(단가) 필드 — 통화 기호 + 천단위/소수 포맷으로 표시 (기호는 테넌트 기본값, 하드코딩 금지)
const CURRENCY_FIELDS = new Set(["stdUnitPrice"]);
// 값 자체가 코드인 필드 — 언어팩으로 번역 (예: altResPolicyCd "fast" → t("fast"))
const CODE_VALUE_FIELDS = new Set(["altResPolicyCd"]);

const formatFieldValue = (field, raw, t) => {
  const value = raw?.[field];
  if (value == null || value === "") return "-";
  // 불리언 플래그(예: discreteFlg)는 원본 true/false 대신 언어별 라벨로 표시
  if (typeof value === "boolean") return t(value ? "floBoolTrue" : "floBoolFalse");
  // 코드 값 필드는 값으로 번역 (키 없으면 i18next가 원본 값 그대로 반환 → 안전)
  if (CODE_VALUE_FIELDS.has(field)) return t(value);
  if (CURRENCY_FIELDS.has(field)) {
    const num = numberUtil.formatNumber(value, { maximumFractionDigits: 2 });
    if (typeof num !== "string") return "-"; // 숫자 아님 → NaN 반환
    // FLO 차트는 품목에 통화코드가 없으므로 테넌트 기본 통화 기호로 폴백된다.
    const symbol = CodeUtil.getCurrencySymbol(raw?.currencyCd) ?? "";
    return `${symbol}${num}`;
  }
  return String(value);
};

const KIND_LABEL_KEY = {
  [FLO_KIND.ITEM]: "floKindItem",
  [FLO_KIND.ROUTE]: "floKindRoute",
  [FLO_KIND.RESOURCE]: "floKindResource",
};

/**
 * 대체 자재 노드일 때만 덧붙는 섹션 — 무엇을 대체하는지 / 어느 공정에 투입되는지 / 택1 우선순위.
 *
 * 투입 공정이 NULL 이면 "지정 없음"으로 표시한다. "모든 공정"처럼 개수를 함의하는 표현은 쓰지 않는다 —
 * 공정이 1개뿐인 품목에서 없는 공정이 더 있는 것처럼 읽히기 때문. 그래프에서 그 대체가 실제로
 * 어디에 연결되는지는 선으로 보이므로, 이 칸은 원본 데이터 상태(지정 여부)만 말하면 된다.
 * 코드가 아닌 값(지정 없음)은 흐린 색으로 구분해 코드와 혼동되지 않게 한다.
 */
const AltSupplyInfo = ({ t, selected }) => {
  const routeCd = selected.altSupplyRouteCd;
  const rows = [
    ["itemCd", selected.altSupplyOf, false],
    ["consmpRouteCd", routeCd ?? t("floAltSupplyNoRoute"), routeCd == null],
    ["priority", selected.altSupplyPriority, false],
  ];

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
        {t("alternateSupply")}
      </Typography>
      <Divider sx={{ my: 0.75 }} />
      {rows.map(([labelKey, value, muted]) => (
        <Box
          key={labelKey}
          sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.75, borderBottom: "1px dashed #eeeeee" }}
        >
          <Typography variant="caption" color="text.secondary">
            {t(labelKey)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: muted ? 400 : 500,
              color: muted ? "text.secondary" : undefined,
              textAlign: "right",
              wordBreak: "break-all",
            }}
          >
            {value == null || value === "" ? "-" : String(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/**
 * 노드 클릭 시 우측에 노드 기본 속성을 표시하는 사이드바.
 */
const FloNodeSidebar = ({ t, selected }) => (
  <Paper variant="outlined" sx={{ width: 280, flexShrink: 0, p: 2, overflowY: "auto" }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
      {t("floNodeInfo")}
    </Typography>
    <Divider sx={{ mb: 2 }} />

    {!selected ? (
      <Typography variant="body2" color="text.secondary">
        {t("floSelectNodeHint")}
      </Typography>
    ) : (
      <Box>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 2 }}>
          <Chip size="small" color="primary" variant="outlined" label={t(KIND_LABEL_KEY[selected.kind])} />
          {selected.isAltSupply && <Chip size="small" color="success" variant="outlined" label={t("alternateSupply")} />}
        </Box>
        {(FIELDS[selected.kind] || []).map((field) => (
          <Box
            key={field}
            sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.75, borderBottom: "1px dashed #eeeeee" }}
          >
            <Typography variant="caption" color="text.secondary">
              {t(FIELD_LABEL_KEY[field] ?? field)}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, textAlign: "right", wordBreak: "break-all" }}>
              {formatFieldValue(field, selected.raw, t)}
            </Typography>
          </Box>
        ))}
        {selected.isAltSupply && <AltSupplyInfo t={t} selected={selected} />}
      </Box>
    )}
  </Paper>
);

export default withTranslation()(FloNodeSidebar);
